import { useEffect, useState } from "react"
import HasPermission, { CanGrantCriticalRoles, CanManageWorkContracts } from "app/PermissionChecker"
import WorkerContractCard from "features/hr/workContract/components/WorkerContractCard"

function capitalizeFirstLetter(str) {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

const EmploymentForm = ({
  mode = "create",           // "create" o "edit"
  initialData = {},         // dati precompilati: { firstname, lastname, username, email, roleGuid, siteGuid?, isEmployee?, workContract? }
  roles = [],
  businessUnits = [],       // solo per edit
  onSubmit,
  isLoading = false,
  t
}) => {
  const hasHighLevelPermission = HasPermission(CanGrantCriticalRoles)
  const hasManageContractPermission = HasPermission(CanManageWorkContracts)

  const [name, setFirstname] = useState(initialData.name || "")
  const [surname, setLastname] = useState(initialData.surname || "")
  const [username, setUsername] = useState(initialData.username || "")
  const [pwd, setPwd] = useState("")
  const [resetPwd, setResetPwd] = useState(mode === "create")
  const [showPwd, setShowPwd] = useState(false)
  const [email, setEmail] = useState(initialData.email || "")
  const [roleGuid, setRoleGuid] = useState(initialData.roleGuid || "")
  const [roleLabel, setRoleLabel] = useState(initialData.roleLabel || "")
  const [businessUnitGuid, setBusinessUnitGuid] = useState(initialData.siteGuid || "")
  const [isEmployee, setIsEmployee] = useState(initialData.isEmployee || false)
  const [workContract, setWorkContract] = useState(initialData.workContract || null)

  // Autocompletion username solo in create
  useEffect(() => {
    if (mode === "create" && name && surname) {
      setUsername(
        name.substring(0, 4).toLowerCase()
        + capitalizeFirstLetter(surname.substring(0, 4))
      )
    }
  }, [name, surname, mode])

  const permittedRoles = hasHighLevelPermission
    ? roles
    : roles.filter(r => !r.role.isCritical)

  const handleSubmit = (e) => {
    e.preventDefault()
    const payload = {
      name: name,
      surname: surname,
      username,
      email,
      ...(resetPwd && { password: pwd }),
      roleGuid,
      roleLabel,
      ...(businessUnitGuid && { businessUnitGuid }),
      isEmployee,
      workContract: isEmployee ? workContract : null
    }
    onSubmit(payload)
  }

  console.log("BusinessUnit selected: " + businessUnitGuid)

  return (
    <form onSubmit={handleSubmit}>
      <h4><i className="fa-solid fa-user"></i> {t("menu.user_info")}</h4>
      <div className="row mb-3">
        <div className="col-md-4 mb-3">
          <label>{t("property.firstname")}</label>
          <input type="text" className="form-control" value={name}
            onChange={e => setFirstname(e.target.value)} required />
        </div>
        <div className="col-md-4 mb-3">
          <label>{t("property.lastname")}</label>
          <input type="text" className="form-control" value={surname}
            onChange={e => setLastname(e.target.value)} required />
        </div>
      </div>


      <div className="row">
        <div className="col-md-4 mb-3">
          <div className="mb-3">
            <label>{t("property.username")}</label>
            <div className="input-group">
              <span className="input-group-text">@</span>
              <input type="text" className="form-control" value={username}
                onChange={e => setUsername(e.target.value)} required />
            </div>
          </div>

          <div className="mb-3">
            <label>{t("property.email")} <span className="text-muted">({t("property.optional")})</span></label>
            <input type="email" className="form-control" value={email}
              onChange={e => setEmail(e.target.value)} />
          </div>
          <div className="mb-3">
            <label>{t("property.password")}</label>
            {resetPwd ? (
              // type="text" + WebkitTextSecurity evita il popup "salva password" di Chrome.
              // Il toggle mostra/nasconde il testo manualmente.
              // Nota: su Firefox -webkit-text-security non ha effetto (campo visibile in chiaro).
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  autoComplete="off"
                  style={{ WebkitTextSecurity: showPwd ? 'none' : 'disc' }}
                  value={pwd}
                  onChange={e => setPwd(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="btn btn btn-secondary"
                  onClick={() => setShowPwd(v => !v)}
                  title={showPwd ? t("action.hide") : t("action.show")}
                >
                  <i className={`fa-solid ${showPwd ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
            ) : (
              <><br /><button type="button" className="btn btn-warning"
                onClick={() => { setResetPwd(true); setPwd(""); setShowPwd(false) }}>
                <i className="fa-solid fa-wrench"></i> {t("helper.reset_password")}
              </button></>
            )}
          </div>
        </div>
      </div>


      <h4><i className="fa-solid fa-briefcase"></i> {t("menu.employment_info")}</h4>
      <div className="row">
        <div className="col-md-4 mb-3">
          <label>{t("property.role")}*</label>
          <select className="form-select" value={roleGuid}
            onChange={e => setRoleGuid(e.target.value)} required>
            <option value="">{t("helper.select_role")}</option>
            {permittedRoles.map(r => (
              <option key={r.role.guid} value={r.role.guid}>{r.role.name}</option>
            ))}
          </select>
        </div>

        <div className="col-md-4 mb-3">
          <label>{t("property.companyRole")}</label>
          <input
            type="text"
            className="form-control"
            value={roleLabel}
            onChange={e => setRoleLabel(e.target.value)}
            placeholder={"es. CEO, Sales Manager, Engineer" }
          />
        </div>

        {businessUnits.length > 0 && (
          <div className="col-md-4 mb-3">
            <label>{t("property.site")}*</label>
            <select className="form-select" value={businessUnitGuid}
              onChange={e => setBusinessUnitGuid(e.target.value)}>
              <option value="">{t("helper.select_site")}</option>
              {businessUnits.map(bu => (
                <option key={bu.businessUnit.guid} value={bu.businessUnit.guid}>
                  {bu.businessUnit.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {hasManageContractPermission && (
        <>
          <div className="form-check mb-3">
            <input className="form-check-input" type="checkbox" checked={isEmployee}
              onChange={e => setIsEmployee(e.target.checked)} />
            <label className="form-check-label">{t("entity.employee")}</label>
          </div>
          {isEmployee && (
            <WorkerContractCard
              initialContract={workContract}
              onValueChanged={setWorkContract}
            />
          )}
        </>
      )}

      <button type="submit" className="btn btn-primary" disabled={isLoading}>
        <i className="fa-solid fa-floppy-disk"></i> {t("action.save")}
      </button>
    </form>
  )
}

export default EmploymentForm
